import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Tent, MapPin, CalendarDays, Plus, Trash2, FileText, Lock, Package, Scale, Save,
  Undo2, Link2, Unlink, Receipt, CheckCircle2, Ban, AlertTriangle, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getDispatches, getReturns, openReturnPdf, openDispatchPdf, voidReturn } from '../../lib/api';
import RecordReturnDialog from '../../components/RecordReturnDialog';
import {
  getEvent, updateEventStatus, listEventDispatches,
  getEventReconciliation, saveEventReconciliation,
  attachDispatchToEvent, detachDispatchFromEvent, pushEventToAccountsIQ, getEventInvoicePreview,
  addFreelinesToDispatch,
} from './api';

const STATUS_FLOW = ['planning', 'active', 'reconciling', 'closed'];
const STATUS_META = {
  planning: { label: 'Planning', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  reconciling: { label: 'Reconciling', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  closed: { label: 'Closed', className: 'bg-zinc-200 text-zinc-700 border-zinc-300' },
};

const UNIT_TYPES = ['units', 'kg', 'cases', 'wheels'];

// Freeline rows: non-batch extras (crisps, t-shirts, ...) appended onto the
// event's attached pipeline dispatch. Internal products ALWAYS arrive via the
// pipeline attach so they keep their batch numbers for recall traceability.
const newRow = () => ({
  key: Math.random().toString(36).slice(2),
  product_name: '',
  quantity: '',
  unit_type: 'units',
});

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [freelineOpen, setFreelineOpen] = useState(false);
  const [rows, setRows] = useState([newRow()]);
  const [targetDispatchId, setTargetDispatchId] = useState('');
  const [saving, setSaving] = useState(false);

  // Reconciliation state
  const [recon, setRecon] = useState(null);
  const [reconEdits, setReconEdits] = useState({}); // key `${dispatch_id}:${line_index}` -> {sold, waste}
  const [reconNotes, setReconNotes] = useState('');
  const [savingRecon, setSavingRecon] = useState(false);

  // Stock Returns
  const [eventReturns, setEventReturns] = useState([]);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDispatch, setReturnDispatch] = useState(null);

  // Attach existing order
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachSearch, setAttachSearch] = useState('');
  const [attachResults, setAttachResults] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);

  // AccountsIQ push
  const [pushing, setPushing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [invoiceBasis, setInvoiceBasis] = useState('net');

  // Void return (admin)
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const { user: authUser } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(authUser?.role);

  // Status stepper
  const [statusChanging, setStatusChanging] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  // Workflow guide navigation targets
  const dispatchesRef = useRef(null);
  const returnsRef = useRef(null);
  const reconRef = useRef(null);
  const invoiceRef = useRef(null);

  const loadRecon = useCallback(async () => {
    try {
      const res = await getEventReconciliation(id);
      setRecon(res.data);
      const edits = {};
      (res.data.lines || []).forEach((l) => {
        edits[`${l.dispatch_id}:${l.line_index}`] = {
          sold: l.sold || 0, waste: l.waste || 0,
        };
      });
      setReconEdits(edits);
      setReconNotes(res.data.notes || '');
    } catch (err) {
      // reconciliation is optional; ignore
    }
  }, [id]);

  const loadReturns = useCallback(async () => {
    try {
      const res = await getReturns({ event_id: id, page_size: 100 });
      setEventReturns(res.data.returns || []);
    } catch (err) {
      // returns register is supplementary; ignore
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, disp] = await Promise.all([getEvent(id), listEventDispatches(id)]);
      setEvent(ev.data);
      setDispatches(disp.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load event');
      navigate('/events');
    } finally {
      setLoading(false);
    }
    await Promise.all([loadRecon(), loadReturns()]);
  }, [id, navigate, loadRecon, loadReturns]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (status) => {
    setStatusChanging(true);
    try {
      await updateEventStatus(id, status);
      toast.success(`Status set to ${STATUS_META[status].label}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusChanging(false);
      setPendingStatus(null);
    }
  };

  // Close / reopen have real consequences → confirm first; other moves are instant.
  const requestStatusChange = (status) => {
    if (status === event?.status) return;
    if (status === 'closed' || event?.status === 'closed') {
      setPendingStatus(status);
    } else {
      handleStatusChange(status);
    }
  };

  const freelineTargets = dispatches.filter((d) => d.status !== 'reversed');

  const openFreelines = () => {
    if (freelineTargets.length === 0) {
      toast.error('Attach a pipeline order first — event items are added onto its dispatch note.');
      return;
    }
    setRows([newRow()]);
    setTargetDispatchId(freelineTargets[0].id);
    setFreelineOpen(true);
  };

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, newRow()]);
  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const handleAddFreelines = async () => {
    const lines = [];
    for (const r of rows) {
      const qty = parseFloat(r.quantity);
      if (!r.product_name.trim() && !r.quantity) continue;
      if (!r.product_name.trim()) {
        toast.error('Enter a name for each item.');
        return;
      }
      if (!qty || qty <= 0) {
        toast.error(`${r.product_name}: enter a quantity.`);
        return;
      }
      lines.push({
        product_name: r.product_name.trim(),
        quantity: qty,
        unit_type: r.unit_type,
      });
    }
    if (lines.length === 0) {
      toast.error('Add at least one item with a quantity.');
      return;
    }
    if (!targetDispatchId) {
      toast.error('Pick the dispatch note to add the items to.');
      return;
    }
    setSaving(true);
    try {
      await addFreelinesToDispatch(id, targetDispatchId, lines);
      toast.success('Items added to the event dispatch note');
      setFreelineOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add items');
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async (dispatchId) => {
    // SEC-001: use the shared download-token helper — no raw JWT in URLs.
    await openDispatchPdf(dispatchId);
  };

  // ── Reconciliation helpers ──
  // Returned is READ-ONLY here: it comes from formal Stock Return records.
  const editFor = (l) => reconEdits[`${l.dispatch_id}:${l.line_index}`] || { sold: 0, waste: 0 };
  const setEdit = (l, patch) => {
    const key = `${l.dispatch_id}:${l.line_index}`;
    setReconEdits((prev) => ({ ...prev, [key]: { ...editFor(l), ...patch } }));
  };
  const usedOf = (l) => Math.max((l.dispatched || 0) - (l.returned || 0), 0);
  const sampledOf = (l) => {
    const e = editFor(l);
    return Math.max(usedOf(l) - (parseFloat(e.sold) || 0) - (parseFloat(e.waste) || 0), 0);
  };
  const markAllAsSamples = () => {
    setReconEdits((prev) => {
      const next = { ...prev };
      (recon?.lines || []).forEach((l) => {
        const key = `${l.dispatch_id}:${l.line_index}`;
        next[key] = { sold: 0, waste: 0 };
      });
      return next;
    });
    toast.info('All used stock marked as Samples');
  };
  const saveRecon = async ({ silent = false } = {}) => {
    const lines = (recon?.lines || []).map((l) => {
      const e = editFor(l);
      return {
        dispatch_id: l.dispatch_id,
        line_index: l.line_index,
        sold: parseFloat(e.sold) || 0,
        waste: parseFloat(e.waste) || 0,
      };
    });
    setSavingRecon(true);
    try {
      const res = await saveEventReconciliation(id, {
        lines,
        notes: reconNotes || null,
      });
      setRecon(res.data);
      if (!silent) toast.success('Reconciliation saved');
      await load();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save reconciliation');
      return false;
    } finally {
      setSavingRecon(false);
    }
  };
  const handleSaveRecon = () => saveRecon();

  // True when on-screen reconciliation figures differ from what's saved —
  // the invoice preview only ever sees SAVED figures, so we auto-save first.
  const reconDirty = () => {
    if (!recon?.lines?.length) return false;
    return recon.lines.some((l) => {
      const e = editFor(l);
      return (parseFloat(e.sold) || 0) !== (l.sold || 0) || (parseFloat(e.waste) || 0) !== (l.waste || 0);
    });
  };

  // ── Attach existing order ──
  const runAttachSearch = async () => {
    setAttachLoading(true);
    try {
      const res = await getDispatches(attachSearch.trim() ? { search: attachSearch.trim() } : {});
      const list = (res.data || []).filter((d) => d.destination_type !== 'event' && d.status !== 'reversed');
      setAttachResults(list.slice(0, 25));
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setAttachLoading(false);
    }
  };
  const handleAttach = async (dispatchId) => {
    setAttaching(true);
    try {
      const res = await attachDispatchToEvent(id, dispatchId);
      toast.success(res.data.message || 'Order attached to event');
      setAttachOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to attach order');
    } finally {
      setAttaching(false);
    }
  };
  const handleDetach = async (dispatchId) => {
    try {
      const res = await detachDispatchFromEvent(id, dispatchId);
      toast.success(res.data.message || 'Order restored to its customer');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to detach order');
    }
  };

  // ── AccountsIQ posting (preview first, then confirm) ──
  const loadPreview = async (basis) => {
    setPreviewLoading(true);
    try {
      const res = await getEventInvoicePreview(id, basis);
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to build invoice preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  const openInvoicePreview = async () => {
    // The preview is built from the SAVED reconciliation — if the figures on
    // screen changed (e.g. Sold quantities typed but not saved), save first
    // so Mary previews exactly what she's looking at.
    if (!isClosed && reconDirty()) {
      const ok = await saveRecon({ silent: true });
      if (!ok) return;
      toast.info('Reconciliation saved — the preview uses your latest figures.');
    }
    setPreviewOpen(true);
    await loadPreview(invoiceBasis);
  };
  const changeBasis = (b) => {
    if (b === invoiceBasis || previewLoading) return;
    setInvoiceBasis(b);
    loadPreview(b);
  };
  const handlePushInvoice = async () => {
    setPushing(true);
    try {
      const res = await pushEventToAccountsIQ(id, invoiceBasis);
      toast.success(res.data.message || 'Invoice posted to AccountsIQ');
      setPreviewOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post invoice');
    } finally {
      setPushing(false);
    }
  };

  // ── Void return (admin) ──
  const handleVoidReturn = async () => {
    if (!voidTarget) return;
    if (voidReason.trim().length < 5) {
      toast.error('Enter a reason (min 5 characters).');
      return;
    }
    setVoiding(true);
    try {
      const res = await voidReturn(voidTarget.id, { reason: voidReason.trim() });
      toast.success(res.data.message || 'Return voided');
      setVoidTarget(null);
      setVoidReason('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void return');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!event) return null;

  const isClosed = event.status === 'closed';
  const dispatchLineCount = dispatches.reduce((n, d) => n + (d.lines?.length || 0), 0);

  // ── Workflow guide: quick pointers through the event lifecycle ──
  const activeReturns = eventReturns.filter((r) => !r.voided);
  const reconSaved = !!recon?.reconciled_at;
  const guideSteps = [
    { key: 'stock', label: 'Attach pipeline order', done: dispatches.length > 0, target: dispatchesRef,
      hint: 'Attach the sales order from the pipeline (batches included) — then add freeline extras.' },
    { key: 'returns', label: 'Record returns', done: activeReturns.length > 0, optional: true,
      target: eventReturns.length > 0 ? returnsRef : dispatchesRef,
      hint: 'Stock coming back to main store — skip if nothing came back.' },
    { key: 'reconcile', label: 'Reconcile stock', done: reconSaved, target: reconRef,
      hint: 'Split used stock into Sold / Waste / Samples — quantities only.' },
    { key: 'close', label: 'Close event', done: isClosed, action: () => requestStatusChange('closed'),
      hint: 'Makes the figures final and unlocks invoice posting.' },
    { key: 'post', label: 'Post invoice', done: !!event.accountsiq_tx_id, target: invoiceRef,
      hint: 'Preview the quantity lines, then post to AccountsIQ once — pricing is applied there.' },
  ];
  const nextStepKey = (guideSteps.find((s) => !s.done && !s.optional) || {}).key;
  const goToStep = (s) => {
    if (s.action && !s.done) { s.action(); return; }
    s.target?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      {/* ── Persistent header: title + status stepper + workflow pointers ── */}
      <div
        className="sticky -top-3 sm:-top-4 lg:-top-6 z-20 -mx-3 sm:-mx-4 lg:-mx-6 -mt-3 sm:-mt-4 lg:-mt-6 px-3 sm:px-4 lg:px-6 pt-3 pb-2.5 bg-background border-b border-border space-y-2.5 shadow-sm"
        data-testid="event-sticky-header"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="shrink-0 -ml-2" onClick={() => navigate('/events')} data-testid="back-to-events">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-bold tracking-tight flex items-center gap-2 min-w-0">
              <Tent className="w-5 h-5 text-emerald-600 shrink-0" />
              <span className="truncate">{event.name}</span>
              {isClosed && <Lock className="w-4 h-4 text-muted-foreground shrink-0" />}
            </h1>
            <div className="hidden md:flex text-xs text-muted-foreground gap-x-3 items-center min-w-0">
              {event.location && (
                <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {event.location}</span>
              )}
              {(event.start_date || event.end_date) && (
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <CalendarDays className="w-3 h-3" />
                  {event.start_date || '—'} {event.end_date ? `→ ${event.end_date}` : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div
              className="flex items-center gap-0.5 rounded-full border bg-white p-1 shadow-sm"
              data-testid="event-status-stepper"
            >
              {STATUS_FLOW.map((s, i) => {
                const active = event.status === s;
                const locked = statusChanging || (isClosed && !isAdmin);
                return (
                  <React.Fragment key={s}>
                    {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
                    <button
                      type="button"
                      onClick={() => requestStatusChange(s)}
                      disabled={active || locked}
                      title={active ? `Current status: ${STATUS_META[s].label}` : `Set status to ${STATUS_META[s].label}`}
                      className={
                        'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ' +
                        (active
                          ? `${STATUS_META[s].className} border cursor-default`
                          : locked
                            ? 'text-muted-foreground/50 cursor-not-allowed'
                            : 'text-muted-foreground hover:bg-slate-100 hover:text-foreground cursor-pointer')
                      }
                      data-testid={active ? 'event-detail-status' : `event-status-step-${s}`}
                    >
                      {s === 'closed' && <Lock className="w-3 h-3 inline mr-1 -mt-0.5" />}
                      {STATUS_META[s].label}
                    </button>
                  </React.Fragment>
                );
              })}
              {statusChanging && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground ml-1" />}
            </div>
            <span className="text-[10px] text-muted-foreground pr-2">
              {isClosed
                ? (isAdmin ? 'Closed — figures final. Click a stage to reopen (admin).' : 'Closed — figures final. Ask an admin to reopen.')
                : 'Click a stage to move the event along.'}
            </span>
          </div>
        </div>

        {/* Quick pointers — click a step to jump to it */}
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="event-workflow-guide">
          {guideSteps.map((s, i) => {
            const isNext = s.key === nextStepKey;
            return (
              <React.Fragment key={s.key}>
                {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground/30 shrink-0" />}
                <button
                  type="button"
                  onClick={() => goToStep(s)}
                  title={s.hint}
                  className={
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ' +
                    (s.done
                      ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                      : isNext
                        ? 'text-blue-700 bg-blue-50 ring-1 ring-blue-300 hover:bg-blue-100'
                        : 'text-muted-foreground hover:bg-slate-100')
                  }
                  data-testid={`workflow-step-${s.key}`}
                >
                  {s.done ? (
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  ) : (
                    <span className={
                      'w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 ' +
                      (isNext ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600')
                    }>
                      {i + 1}
                    </span>
                  )}
                  {s.label}
                  {s.optional && !s.done && <span className="text-[9px] opacity-60">(if any)</span>}
                  {isNext && <span className="text-[9px] font-bold uppercase tracking-wide">← next</span>}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {event.notes && (
        <Card><CardContent className="py-4 text-sm text-muted-foreground">{event.notes}</CardContent></Card>
      )}

      <Card ref={dispatchesRef} className="scroll-mt-36" data-testid="event-dispatches-card">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" /> Stock Dispatched to Event
            </CardTitle>
            <CardDescription>
              Internal products come from the attached pipeline order (they keep their batch numbers
              for recall traceability). Add freeline extras — crisps, t-shirts — onto the same note.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setAttachOpen(true); setAttachSearch(''); setAttachResults([]); }} disabled={isClosed} data-testid="attach-order-btn">
              <Link2 className="w-4 h-4 mr-2" /> Attach Pipeline Order
            </Button>
            <Button
              variant="outline"
              onClick={openFreelines}
              disabled={isClosed}
              title={freelineTargets.length === 0 ? 'Attach a pipeline order first' : 'Add non-batch extras onto the dispatch note'}
              data-testid="add-freeline-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Event Items
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {dispatches.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground" data-testid="no-event-dispatches">
              No stock attached yet. Attach the sales order from the pipeline — internal products keep
              their batch numbers for recall traceability. Freeline extras are added afterwards.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dispatch #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-center">Lines</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatches.map((d) => (
                  <TableRow key={d.id} data-testid={`event-dispatch-row-${d.id}`}>
                    <TableCell className="font-medium">
                      {d.dispatch_number}
                      {d.migrated_from_customer && (
                        <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200 text-[10px]" title={`Migrated from ${d.migrated_from_customer.customer_name}`}>
                          from {d.migrated_from_customer.customer_name}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{d.dispatch_date || '—'}</TableCell>
                    <TableCell className="text-center">{d.lines?.length || 0}</TableCell>
                    <TableCell><Badge variant="outline">{d.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {!isClosed && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => { setReturnDispatch(d); setReturnDialogOpen(true); }}
                            data-testid={`event-record-return-${d.id}`}
                          >
                            <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Record Return
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openPdf(d.id)} data-testid={`event-dispatch-pdf-${d.id}`}>
                          <FileText className="w-3.5 h-3.5 mr-1.5" /> Dispatch Note
                        </Button>
                        {d.migrated_from_customer && !isClosed && (
                          d.has_returns ? (
                            <Button
                              variant="ghost" size="sm" className="text-muted-foreground/40 cursor-not-allowed"
                              title="Cannot detach — returns are recorded against this dispatch (batch trail is tied to it)"
                              onClick={() => toast.error('This dispatch has returns recorded against it — the batch return trail is tied to it. An admin must void the returns first if the attachment was a mistake.')}
                              data-testid={`event-detach-blocked-${d.id}`}
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost" size="sm" className="text-muted-foreground"
                              title={`Detach — restore to ${d.migrated_from_customer.customer_name}`}
                              onClick={() => handleDetach(d.id)}
                              data-testid={`event-detach-${d.id}`}
                            >
                              <Unlink className="w-3.5 h-3.5" />
                            </Button>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {dispatches.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {dispatches.length} dispatch(es), {dispatchLineCount} line(s) sent to this event.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation */}
      {recon && recon.lines && recon.lines.length > 0 && (
        <Card ref={reconRef} className="scroll-mt-36" data-testid="event-reconciliation-card">
          <CardHeader>
            <div className="flex flex-row items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5" /> Reconciliation — Stock Used
                </CardTitle>
                <CardDescription>
                  <strong>Returned is read-only</strong> — record returns with the “Record Return” button above
                  (each creates a numbered Return Note). <strong>Used = Dispatched − Returned.</strong> Split used
                  stock into Sold / Waste; the rest is recorded as Samples (required for Revenue).
                  Quantities only — pricing is handled in AccountsIQ.
                </CardDescription>
              </div>
              {!isClosed && (
                <Button variant="outline" size="sm" onClick={markAllAsSamples} data-testid="mark-all-samples-btn">
                  Mark all Used as Samples
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Dispatched</TableHead>
                    <TableHead className="text-right w-28">Returned</TableHead>
                    <TableHead className="text-right">Used</TableHead>
                    <TableHead className="text-right w-24">Sold</TableHead>
                    <TableHead className="text-right w-24">Waste</TableHead>
                    <TableHead className="text-right">Samples</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recon.lines.map((l) => {
                    const e = editFor(l);
                    return (
                      <TableRow key={`${l.dispatch_id}:${l.line_index}`} data-testid={`recon-row-${l.dispatch_id}-${l.line_index}`}>
                        <TableCell className="font-medium">
                          {l.product_name}
                          {l.is_adhoc && <Badge variant="secondary" className="ml-2 text-[10px]">freeline</Badge>}
                          <div className="text-xs text-muted-foreground">
                            {l.dispatch_number}
                            {l.batch_code && (
                              <span className="ml-2 font-mono text-blue-700" data-testid={`recon-batch-${l.dispatch_id}-${l.line_index}`}>
                                Batch {l.batch_code}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{(l.dispatched || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <span
                            className={(l.returned || 0) > 0 ? 'font-semibold text-amber-700' : 'text-muted-foreground'}
                            data-testid={`recon-returned-${l.dispatch_id}-${l.line_index}`}
                          >
                            {(l.returned || 0).toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{usedOf(l).toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number" min="0" step="any" className="h-8 text-right"
                            value={e.sold}
                            disabled={isClosed}
                            onChange={(ev) => setEdit(l, { sold: ev.target.value })}
                            data-testid={`recon-sold-${l.dispatch_id}-${l.line_index}`}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number" min="0" step="any" className="h-8 text-right"
                            value={e.waste}
                            disabled={isClosed}
                            onChange={(ev) => setEdit(l, { waste: ev.target.value })}
                            data-testid={`recon-waste-${l.dispatch_id}-${l.line_index}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold text-emerald-700">{sampledOf(l).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="grid gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="recon-notes">Notes</Label>
                <Input
                  id="recon-notes"
                  value={reconNotes}
                  disabled={isClosed}
                  onChange={(e) => setReconNotes(e.target.value)}
                  placeholder="Optional reconciliation notes"
                  data-testid="recon-notes-input"
                />
              </div>
            </div>

            {recon.totals && (
              <div className="flex flex-wrap gap-4 text-sm bg-muted/30 rounded-lg p-3" data-testid="recon-totals">
                <span>Dispatched: <strong>{recon.totals.dispatched.toFixed(2)}</strong></span>
                <span>Used: <strong>{recon.lines.reduce((s, l) => s + usedOf(l), 0).toFixed(2)}</strong></span>
                <span>Sold: <strong>{recon.lines.reduce((s, l) => s + (parseFloat(editFor(l).sold) || 0), 0).toFixed(2)}</strong></span>
                <span>Waste: <strong>{recon.lines.reduce((s, l) => s + (parseFloat(editFor(l).waste) || 0), 0).toFixed(2)}</strong></span>
                <span className="text-emerald-700">Samples: <strong>{recon.lines.reduce((s, l) => s + sampledOf(l), 0).toFixed(2)}</strong></span>
              </div>
            )}

            {!isClosed && (
              <div className="flex justify-end">
                <Button onClick={handleSaveRecon} disabled={savingRecon} data-testid="save-reconciliation-btn">
                  {savingRecon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Reconciliation
                </Button>
              </div>
            )}
            {isClosed && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> This event is closed. Reconciliation is locked for audit integrity.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stock Returns register for this event */}
      {eventReturns.length > 0 && (
        <Card ref={returnsRef} className="scroll-mt-36" data-testid="event-returns-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-amber-600" /> Stock Returns — Returned to Main Store
            </CardTitle>
            <CardDescription>
              Formal, numbered records of stock that came back from this event. Each has a
              printable Return Note PDF (Revenue audit evidence).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Return #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Dispatch #</TableHead>
                  <TableHead className="text-center">Lines</TableHead>
                  <TableHead className="text-right">Total Qty</TableHead>
                  <TableHead>Recorded By</TableHead>
                  <TableHead className="text-right">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eventReturns.map((r) => (
                  <TableRow key={r.id} className={r.voided ? 'opacity-60' : ''} data-testid={`event-return-row-${r.return_number}`}>
                    <TableCell className={`font-mono font-medium ${r.voided ? 'line-through' : ''}`}>
                      {r.return_number}
                      {r.voided && (
                        <Badge variant="outline" className="ml-2 bg-gray-200 text-gray-600 border-gray-300 text-[10px]" title={`Voided: ${r.void_reason || ''}`} data-testid={`voided-badge-${r.return_number}`}>
                          VOIDED
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{r.return_date}</TableCell>
                    <TableCell className="font-mono text-sm">{r.dispatch_number}</TableCell>
                    <TableCell className="text-center">{r.lines?.length || 0}</TableCell>
                    <TableCell className={`text-right font-mono ${r.voided ? 'line-through' : ''}`}>{(r.total_quantity_returned || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.created_by_email}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => openReturnPdf(r.id)}
                          data-testid={`event-return-pdf-${r.return_number}`}
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" /> Return Note
                        </Button>
                        {isAdmin && !r.voided && (
                          <Button
                            variant="ghost" size="sm" className="text-red-600 hover:text-red-700"
                            title="Void this return (admin)"
                            onClick={() => { setVoidTarget(r); setVoidReason(''); }}
                            data-testid={`event-void-return-${r.return_number}`}
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* AccountsIQ totalled invoice */}
      {recon && recon.lines && recon.lines.length > 0 && (
        <Card ref={invoiceRef} className="scroll-mt-36" data-testid="event-invoice-card">
          <CardHeader>
            <div className="flex flex-row items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-700" /> AccountsIQ Invoice
                </CardTitle>
                <CardDescription>
                  Posted ONCE — after all returns are recorded, the reconciliation is saved and the
                  event is <strong>closed</strong>. Stock quantities only (draft, prices 0.00) —
                  pricing is applied in AccountsIQ per customer &amp; product. Any later amendments
                  are made directly in AccountsIQ.
                </CardDescription>
              </div>
              {event.accountsiq_tx_id ? (
                <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="event-invoice-status">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Posted — Tx {event.accountsiq_tx_id}
                </Badge>
              ) : event.accountsiq_status === 'failed' ? (
                /(disabled|not configured)/i.test(event.accountsiq_last_error || '') ? (
                  <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200" data-testid="event-invoice-status">
                    AccountsIQ not configured
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200" data-testid="event-invoice-status">
                    Post failed
                  </Badge>
                )
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-2 md:grid-cols-3 text-sm">
              <div>
                <span className="text-muted-foreground">Invoice Customer:</span>{' '}
                <strong data-testid="event-invoice-customer">{event.invoice_customer_name || 'Not set — edit the event'}</strong>
              </div>
              <div>
                <span className="text-muted-foreground">Pricing:</span>{' '}
                <strong data-testid="event-invoice-pricing-note">Applied in AccountsIQ</strong>
              </div>
              <div>
                <span className="text-muted-foreground">Posted:</span>{' '}
                <strong>{event.accountsiq_pushed_at ? `${event.accountsiq_pushed_at.slice(0, 10)} by ${event.accountsiq_pushed_by || ''}` : 'Not yet'}</strong>
              </div>
            </div>
            {event.accountsiq_last_error && !event.accountsiq_tx_id && (
              <p className="text-xs text-red-600" data-testid="event-invoice-error">{event.accountsiq_last_error}</p>
            )}
            {!event.accountsiq_tx_id && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {!isClosed && (
                  <p className="text-xs text-amber-700 flex items-center gap-1.5" data-testid="invoice-not-closed-hint">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Posting unlocks once the event is closed (returns + reconciliation final).
                  </p>
                )}
                <Button className="ml-auto" onClick={openInvoicePreview} data-testid="push-invoice-btn">
                  <Receipt className="w-4 h-4 mr-2" />
                  Preview & Post Invoice
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Event Items (freelines) dialog */}
      <Dialog open={freelineOpen} onOpenChange={setFreelineOpen}>
        <DialogContent className="max-w-3xl" data-testid="add-freeline-dialog">
          <DialogHeader>
            <DialogTitle>Add Event Items — {event.name}</DialogTitle>
            <DialogDescription>
              Freeline extras (crisps, t-shirts, merch…) added onto the event&apos;s dispatch note —
              the final note is identical from here and the order pipeline. Internal cheese products
              must come via “Attach Pipeline Order” so they keep their batch numbers.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1 max-h-[60vh] overflow-y-auto pr-1">
            {freelineTargets.length > 1 && (
              <div className="w-72">
                <Label className="text-xs">Add onto dispatch note</Label>
                <Select value={targetDispatchId} onValueChange={setTargetDispatchId}>
                  <SelectTrigger className="h-9 text-sm" data-testid="freeline-target-select">
                    <SelectValue placeholder="Pick a dispatch" />
                  </SelectTrigger>
                  <SelectContent>
                    {freelineTargets.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        #{d.dispatch_number} — {d.lines?.length || 0} line(s)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {freelineTargets.length === 1 && (
              <p className="text-xs text-muted-foreground">
                Items will be appended to dispatch note <strong>#{freelineTargets[0].dispatch_number}</strong>.
              </p>
            )}

            <div className="space-y-3">
              {rows.map((r, idx) => (
                <div key={r.key} className="border rounded-lg p-3 bg-muted/20" data-testid={`freeline-row-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 md:col-span-6">
                      <Label className="text-xs">Item name</Label>
                      <Input
                        value={r.product_name}
                        onChange={(e) => updateRow(r.key, { product_name: e.target.value })}
                        placeholder="e.g. Keogh's Crisps"
                        data-testid={`freeline-name-${idx}`}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number" min="0" step="any"
                        value={r.quantity}
                        onChange={(e) => updateRow(r.key, { quantity: e.target.value })}
                        data-testid={`freeline-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-3">
                      <Label className="text-xs">Unit</Label>
                      <Select value={r.unit_type} onValueChange={(v) => updateRow(r.key, { unit_type: v })}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {UNIT_TYPES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4 md:col-span-1 flex justify-end">
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeRow(r.key)} data-testid={`remove-freeline-row-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button variant="outline" size="sm" onClick={addRow} data-testid="add-freeline-row">
              <Plus className="w-4 h-4 mr-2" /> Add Line
            </Button>
          </div>

          <DialogFooter>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setFreelineOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleAddFreelines} disabled={saving} data-testid="confirm-add-freelines-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add to Dispatch Note
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach Existing Order dialog */}
      <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
        <DialogContent className="max-w-2xl" data-testid="attach-order-dialog">
          <DialogHeader>
            <DialogTitle>Attach Existing Order to {event.name}</DialogTitle>
            <DialogDescription>
              Convert a sales order that was already created (e.g. under a “TRADE SHOW” customer)
              into an event dispatch. The original customer is preserved for audit and the order
              can be detached later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex gap-2">
              <Input
                placeholder="Search by dispatch #, customer or PO…"
                value={attachSearch}
                onChange={(e) => setAttachSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runAttachSearch()}
                data-testid="attach-search-input"
              />
              <Button onClick={runAttachSearch} disabled={attachLoading} data-testid="attach-search-btn">
                {attachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border rounded-md">
              {attachResults.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground" data-testid="attach-empty">
                  {attachLoading ? 'Searching…' : 'Search for the order you want to attach.'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dispatch #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>PO</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attachResults.map((d) => (
                      <TableRow key={d.id} data-testid={`attach-result-${d.dispatch_number}`}>
                        <TableCell className="font-mono font-medium">{d.dispatch_number}</TableCell>
                        <TableCell className="text-sm">{d.customer_name}</TableCell>
                        <TableCell className="font-mono text-sm">{d.po_number || '—'}</TableCell>
                        <TableCell className="text-sm">{d.dispatch_date || '—'}</TableCell>
                        <TableCell><Badge variant="outline">{d.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => handleAttach(d.id)}
                            disabled={attaching}
                            data-testid={`attach-confirm-${d.dispatch_number}`}
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1.5" /> Attach
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Return dialog (shared core component) */}
      <RecordReturnDialog
        dispatch={returnDispatch}
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        onSuccess={() => load()}
      />

      {/* Invoice preview & post dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-700" /> Invoice Preview — {event.name}
            </DialogTitle>
            <DialogDescription>
              Exactly what will post to AccountsIQ. Check it carefully — posting happens once.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : preview ? (
            <div className="space-y-4">
              {preview.blockers?.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="invoice-blockers">
                  {preview.blockers.map((b, i) => (
                    <p key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {b}
                    </p>
                  ))}
                </div>
              )}
              <div className="grid gap-2 md:grid-cols-2 text-sm">
                <div><span className="text-muted-foreground">Customer:</span> <strong>{preview.customer_name || '—'}</strong>{preview.customer_code ? ` (${preview.customer_code})` : ''}</div>
                <div><span className="text-muted-foreground">Invoice Date:</span> <strong>{preview.invoice_date}</strong></div>
                <div><span className="text-muted-foreground">Reference:</span> <strong className="font-mono">{preview.external_reference}</strong></div>
                <div><span className="text-muted-foreground">Event Status:</span> <strong className="capitalize">{preview.event_status}</strong></div>
              </div>
              <div className="flex items-center gap-2" data-testid="invoice-basis-toggle">
                <span className="text-xs text-muted-foreground">Quantity basis:</span>
                <div className="flex rounded-md border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => changeBasis('net')}
                    className={
                      'px-3 py-1.5 text-xs font-medium transition-colors ' +
                      (invoiceBasis === 'net' ? 'bg-blue-600 text-white' : 'bg-white text-muted-foreground hover:bg-slate-50')
                    }
                    data-testid="invoice-basis-net"
                  >
                    Full qty (net of returns)
                  </button>
                  <button
                    type="button"
                    onClick={() => changeBasis('sold')}
                    className={
                      'px-3 py-1.5 text-xs font-medium transition-colors border-l ' +
                      (invoiceBasis === 'sold' ? 'bg-blue-600 text-white' : 'bg-white text-muted-foreground hover:bg-slate-50')
                    }
                    data-testid="invoice-basis-sold"
                  >
                    Sold only
                  </button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(preview.lines || []).map((l, i) => (
                    <TableRow key={i} data-testid={`invoice-line-${i}`}>
                      <TableCell className="text-sm">{l.Description}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{l.Quantity}</TableCell>
                    </TableRow>
                  ))}
                  {(preview.lines || []).length === 0 && (
                    <TableRow><TableCell colSpan={2} className="text-center text-sm text-muted-foreground py-6">No invoice lines yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
                <p className="text-xs text-muted-foreground max-w-sm">{preview.pricing_note}</p>
                <span>Total Quantity: <strong className="font-mono" data-testid="invoice-total-quantity">{Number(preview.total_quantity || 0).toFixed(2)}</strong></span>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={pushing}>Cancel</Button>
            <Button
              onClick={handlePushInvoice}
              disabled={pushing || previewLoading || !preview || (preview.blockers || []).length > 0}
              data-testid="confirm-post-invoice-btn"
            >
              {pushing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirm & Post to AccountsIQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close / reopen confirmation */}
      <Dialog open={!!pendingStatus} onOpenChange={(open) => { if (!open) setPendingStatus(null); }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="status-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-blue-700" />
              {pendingStatus === 'closed'
                ? 'Close this event?'
                : `Reopen event as ${pendingStatus ? STATUS_META[pendingStatus]?.label : ''}?`}
            </DialogTitle>
            <DialogDescription>
              {pendingStatus === 'closed' ? (
                <>
                  Closing makes the figures FINAL: no further returns, voids or reconciliation edits — and the AccountsIQ invoice posting unlocks. You can reopen later as an admin if a correction is needed.
                </>
              ) : (
                <>
                  Reopening lets returns, voids and reconciliation edits happen again (admin action, audit-logged).
                  {event.accountsiq_tx_id && (
                    <span className="block mt-1.5 text-amber-700 font-medium">
                      Note: the invoice was already posted (Tx {event.accountsiq_tx_id}) — changes made after
                      reopening will NOT update AccountsIQ; amend it there directly.
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingStatus(null)} disabled={statusChanging}>Cancel</Button>
            <Button
              onClick={() => handleStatusChange(pendingStatus)}
              disabled={statusChanging}
              data-testid="confirm-status-change-btn"
            >
              {statusChanging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {pendingStatus === 'closed' ? 'Close Event' : 'Reopen Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void return dialog (admin) */}
      <Dialog open={!!voidTarget} onOpenChange={(open) => { if (!open) setVoidTarget(null); }}>
        <DialogContent className="sm:max-w-[460px]" data-testid="event-void-return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Ban className="w-5 h-5" /> Void Return {voidTarget?.return_number}
            </DialogTitle>
            <DialogDescription>
              For returns recorded in error. Quantities roll back off the dispatch and the
              reconciliation auto-corrects. The record stays in the register, watermarked VOID.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="event-void-reason">Reason *</Label>
            <Textarea
              id="event-void-reason" rows={2} maxLength={500}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Wrong quantity entered — actual return was 6, not 8"
              data-testid="event-void-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)} disabled={voiding}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleVoidReturn}
              disabled={voiding || voidReason.trim().length < 5}
              data-testid="event-confirm-void-btn"
            >
              {voiding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Void Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
